import React, { useEffect, useState } from "react";
import { Table, Card, Typography, Tag, Input, Button, Row, Col, Select, DatePicker, Avatar, Space, Tooltip } from "antd";
import { SearchOutlined, DownloadOutlined, FilterOutlined, UserOutlined, GlobalOutlined } from "@ant-design/icons";
import axiosInstance from "../../configs";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function UserActivityLog() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({
        module: "All Modules",
        action: "All Actions",
        dateRange: null
    });

    const fetchData = async (search = "") => {
        setLoading(true);
        try {
            // Ensure backend supports these params or filtration happens on client for now
            const res = await axiosInstance.get("/logs", {
                params: { limit: 100, search },
            });
            setData(res.data || []);
        } catch (e) {
            console.error("Failed to load logs", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchText(val);
        if (val.length === 0) fetchData();
    };

    const onSearchSubmit = () => fetchData(searchText);

    const openMap = (lat, long) => {
        if (!lat || !long) return;
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${long}`, "_blank");
    };

    const columns = [
        {
            title: "TIMESTAMP",
            key: "timestamp",
            width: 140,
            render: (_, r) => (
                <div style={{ lineHeight: "1.2" }}>
                    <Text strong style={{ display: "block" }}>{dayjs(r.tgl).format("YYYY-MM-DD")}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.waktu} WIB</Text>
                </div>
            ),
        },
        {
            title: "ACTOR",
            key: "actor",
            width: 200,
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#fde3cf', color: '#f56a00' }} />
                    <div style={{ lineHeight: "1.2" }}>
                        <Text strong style={{ display: "block" }}>{r.user_name || "System"}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{r.username || "Unknown Role"}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: "ACTION",
            dataIndex: "activity",
            key: "activity",
            width: 100,
            render: (t) => {
                let color = "default";
                let text = t ? t.toUpperCase() : "UNKNOWN";
                if (text === "LOGIN") color = "processing"; // Blueish
                if (text === "UPDATE" || text === "PUT") color = "geekblue";
                if (text === "CREATE" || text === "POST" || text === "INSERT") color = "success";
                if (text === "DELETE") color = "error";
                return <Tag color={color} style={{ fontWeight: 600 }}>{text}</Tag>;
            },
        },
        {
            title: "MODULE",
            dataIndex: "module",
            key: "module",
            width: 150,
            render: (t) => <Text>{t || "General"}</Text>,
        },
        {
            title: "RECORD ID",
            key: "record_id",
            width: 120,
            render: (_, r) => (
                <Text style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 500 }}>
                    {r.record_id || `LOG-${r.id_user_log}`}
                </Text>
            ),
        },
        {
            title: "LONGLAT",
            key: "longlat",
            width: 140,
            render: (_, r) => {
                const lat = r.latitude;
                const long = r.longitude;

                if (!lat || !long) return <Text disabled>-</Text>;

                return (
                    <div
                        onClick={() => openMap(lat, long)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#1890ff' }}
                    >
                        <GlobalOutlined />
                        <Text style={{ color: 'inherit', fontSize: 12 }}>{lat}, {long}</Text>
                    </div>
                );
            }
        },
        {
            title: "IP ADDRESS",
            dataIndex: "ip_address",
            key: "ip_address",
            width: 120,
            render: (t) => <Text type="secondary">{t || "127.0.0.1"}</Text>,
        },
    ];

    const expandedRowRender = (record) => {
        const newValue = tryParse(record.new_value);
        // Mocking previous value for demo purposes as it might not be in the current API
        const prevValue = record.old_value ? tryParse(record.old_value) : { Info: "No previous data recorded" };

        return (
            <Card type="inner" title="CHANGE DETAILS" size="small" style={{ margin: 0, background: '#fafafa' }}>
                <Row gutter={24}>
                    <Col span={12}>
                        <div style={{ background: '#fff1f0', padding: 12, borderRadius: 6, border: '1px solid #ffa39e' }}>
                            <Text type="danger" strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>PREVIOUS VALUE</Text>
                            <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(prevValue, null, 2)}
                            </pre>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ background: '#f6ffed', padding: 12, borderRadius: 6, border: '1px solid #b7eb8f' }}>
                            <Text type="success" strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>NEW VALUE</Text>
                            <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(newValue, null, 2)}
                            </pre>
                        </div>
                    </Col>
                </Row>
            </Card>
        );
    };

    const handleExport = async () => {
        try {
            const res = await axiosInstance.get("/logs/export", {
                params: { search: searchText },
                responseType: "blob", // Important for binary data
            });

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Audit_Log_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (e) {
            console.error("Export failed", e);
        }
    };

    return (
        <div style={{ padding: 24 }}>
            {/* Header Section */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <Title level={2} style={{ margin: 0, fontWeight: 700 }}>Audit Log & Traceability</Title>
                        <Text type="secondary">Immutable record of system events for Customs Compliance.</Text>
                    </div>
                    <Space>
                        <Button icon={<FilterOutlined />}>Advanced Filters</Button>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>Export Log</Button>
                    </Space>
                </div>
            </div>

            {/* Filters Bar */}
            <Card bodyStyle={{ padding: 16 }} style={{ marginBottom: 24, borderRadius: 8 }}>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Input
                            placeholder="Search Record ID, User, or Keyword"
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={searchText}
                            onChange={handleSearch}
                            onPressEnter={onSearchSubmit}
                            size="large"
                        />
                    </Col>
                    <Col>
                        <RangePicker size="large" style={{ width: 250 }} />
                    </Col>
                    <Col>
                        <Select defaultValue="All Modules" size="large" style={{ width: 160 }}>
                            <Option value="All Modules">All Modules</Option>
                            <Option value="Inventory">Inventory</Option>
                            <Option value="Customs">Customs</Option>
                            <Option value="Auth">Authentication</Option>
                        </Select>
                    </Col>
                    <Col>
                        <Select defaultValue="All Actions" size="large" style={{ width: 140 }}>
                            <Option value="All Actions">All Actions</Option>
                            <Option value="CREATE">Create</Option>
                            <Option value="UPDATE">Update</Option>
                            <Option value="DELETE">Delete</Option>
                            <Option value="LOGIN">Login</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Data Table */}
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id_user_log"
                expandable={{
                    expandedRowRender,
                    expandIconColumnIndex: 0,
                }}
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
                scroll={{ x: 1000 }}
            />
        </div>
    );
}

function tryParse(str) {
    if (!str) return null;
    try {
        return JSON.parse(str);
    } catch (e) {
        return str;
    }
}
